import { API_BASE_URL, logging } from '@/config/env';
import type {
  ApiNotificationLogListResponse,
  NotificationLog,
  NotificationLogListResponse,
  NotificationLogQueryParams,
} from './notificationLogs.dto';

const customLog = (...args: unknown[]) => {
  if (logging) {
    console.log(...args);
  }
};

class NotificationLogsService {
  private baseUrl = API_BASE_URL;
  private logoutCallback?: () => void;

  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    customLog('=== API Request ===');
    customLog('URL:', url);
    customLog('Method:', options.method || 'GET');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    customLog('Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      customLog('Error response:', errorText);
      try {
        const errorData = JSON.parse(errorText);
        if (
          errorData.status === false &&
          errorData.message === 'Invalid or expired admin session'
        ) {
          if (this.logoutCallback) {
            this.logoutCallback();
          }
        }
      } catch {
        // ignore parse errors
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    customLog('Response data:', JSON.stringify(data, null, 2));

    if (
      data?.status === false &&
      data?.message === 'Invalid or expired admin session'
    ) {
      if (this.logoutCallback) {
        this.logoutCallback();
      }
    }

    return data;
  }

  async getNotificationLogs(
    token: string,
    params: NotificationLogQueryParams = {}
  ): Promise<NotificationLogListResponse> {
    const queryString = new URLSearchParams();
    if (params.page) queryString.append('page', params.page.toString());
    if (params.limit) queryString.append('limit', params.limit.toString());
    if (params.search) queryString.append('search', params.search);
    if (params.notificationType)
      queryString.append('notificationType', params.notificationType);
    if (params.channel) queryString.append('channel', params.channel);
    if (params.status) queryString.append('status', params.status);
    if (params.sortBy) queryString.append('sortBy', params.sortBy);
    if (params.sortOrder) queryString.append('sortOrder', params.sortOrder);

    const endpoint = `/admin/notification-log${
      queryString.toString() ? `?${queryString.toString()}` : ''
    }`;

    const response = await this.request<ApiNotificationLogListResponse>(
      endpoint,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.data) {
      return {
        data: [],
        meta: { total: 0, page: 1, lastPage: 1, limit: params.limit || 10 },
      };
    }

    const responseData = response.data as any;

    if (Array.isArray(responseData)) {
      return {
        data: responseData,
        meta: {
          total: (response as any).total || 0,
          page: (response as any).page || 1,
          lastPage: (response as any).lastPage || 1,
          limit: params.limit || 10,
        },
      };
    }

    return {
      data: responseData.data || [],
      meta: {
        total:
          responseData.meta?.total ||
          responseData.total ||
          (response as any).total ||
          0,
        page:
          responseData.meta?.page ||
          responseData.page ||
          (response as any).page ||
          1,
        lastPage:
          responseData.meta?.lastPage ||
          responseData.totalPages ||
          (response as any).lastPage ||
          1,
        limit: params.limit || 10,
      },
    };
  }
}

export const notificationLogsService = new NotificationLogsService();
